import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from '@/components/ui/sonner';
import { runContentDiagnostics, fixMissingContent, ContentDiagnostics } from '@/services/contentDiagnostics';
import { restartQuizGeneration } from '@/services/restartQuizGeneration';
import { restartPracticeGeneration } from '@/services/restartPracticeGeneration';
import { AlertCircle, CheckCircle2, RefreshCw, Wrench } from 'lucide-react';

export const ContentDiagnosticsPanel = () => {
  const [diagnostics, setDiagnostics] = useState<ContentDiagnostics | null>(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [retrying, setRetrying] = useState<'quiz' | 'practice' | null>(null);

  const handleRunDiagnostics = async () => {
    try {
      setLoading(true);
      const results = await runContentDiagnostics();
      setDiagnostics(results);
      toast.success('Diagnostics complete');
    } catch (error) {
      toast.error('Failed to run diagnostics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFixContent = async () => {
    try {
      setFixing(true);
      await fixMissingContent();
      toast.success('Content fix initiated');
      await handleRunDiagnostics();
    } catch (error) {
      toast.error('Failed to fix content');
      console.error(error);
    } finally {
      setFixing(false);
    }
  };

  const handleRetryQuizzes = async () => {
    try {
      setRetrying('quiz');
      await restartQuizGeneration();
      toast.success('Quiz generation restarted');
    } catch (error) {
      toast.error('Failed to restart quiz generation');
      console.error(error);
    } finally {
      setRetrying(null);
    }
  };

  const handleRetryPractice = async () => {
    try {
      setRetrying('practice');
      await restartPracticeGeneration();
      toast.success('Practice lab generation restarted');
    } catch (error) {
      toast.error('Failed to restart practice generation');
      console.error(error);
    } finally {
      setRetrying(null);
    }
  };

  const contentHealthPercentage = diagnostics 
    ? (diagnostics.modulesWithContent / diagnostics.totalModules) * 100 
    : 0;

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Content Health Diagnostics</h3>
          <Button
            onClick={handleRunDiagnostics}
            disabled={loading}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Run Diagnostics
          </Button>
        </div>

        {diagnostics && (
          <>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Content Coverage</span>
                  <span className="text-sm text-muted-foreground">
                    {diagnostics.modulesWithContent}/{diagnostics.totalModules} modules
                  </span>
                </div>
                <Progress value={contentHealthPercentage} className="h-2" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Quiz Success Rate</span>
                    <span className={`text-sm ${diagnostics.quizSuccessRate > 95 ? 'text-green-600' : 'text-yellow-600'}`}>
                      {diagnostics.quizSuccessRate.toFixed(1)}%
                    </span>
                  </div>
                  <Progress value={diagnostics.quizSuccessRate} className="h-2" />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Practice Lab Success Rate</span>
                    <span className={`text-sm ${diagnostics.practiceSuccessRate > 95 ? 'text-green-600' : 'text-yellow-600'}`}>
                      {diagnostics.practiceSuccessRate.toFixed(1)}%
                    </span>
                  </div>
                  <Progress value={diagnostics.practiceSuccessRate} className="h-2" />
                </div>
              </div>

              {diagnostics.modulesWithoutContent > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium text-yellow-900">
                        {diagnostics.modulesWithoutContent} modules missing content
                      </h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        These modules need content before generation can succeed
                      </p>
                      {diagnostics.missingContentModules.length > 0 && (
                        <div className="mt-2 text-xs text-yellow-600 font-mono bg-yellow-100 p-2 rounded max-h-32 overflow-y-auto">
                          {diagnostics.missingContentModules.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {diagnostics.quizSuccessRate === 100 && diagnostics.practiceSuccessRate === 100 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-green-900">All systems operational</h4>
                      <p className="text-sm text-green-700 mt-1">
                        100% success rate for both quizzes and practice labs
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-4 border-t">
              <Button
                onClick={handleFixContent}
                disabled={fixing || diagnostics.modulesWithoutContent === 0}
                variant="secondary"
                size="sm"
              >
                <Wrench className="h-4 w-4 mr-2" />
                {fixing ? 'Fixing...' : 'Fix Missing Content'}
              </Button>

              <Button
                onClick={handleRetryQuizzes}
                disabled={retrying === 'quiz' || diagnostics.quizSuccessRate === 100}
                variant="secondary"
                size="sm"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${retrying === 'quiz' ? 'animate-spin' : ''}`} />
                Retry Quizzes
              </Button>

              <Button
                onClick={handleRetryPractice}
                disabled={retrying === 'practice' || diagnostics.practiceSuccessRate === 100}
                variant="secondary"
                size="sm"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${retrying === 'practice' ? 'animate-spin' : ''}`} />
                Retry Practice Labs
              </Button>
            </div>
          </>
        )}

        {!diagnostics && !loading && (
          <div className="text-center py-8 text-muted-foreground">
            <p>Click "Run Diagnostics" to analyze content health</p>
          </div>
        )}
      </div>
    </Card>
  );
};
